import { Package, FileText, Activity } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Card } from '@/components/ui/card/Card';
import type { Tool } from '@/features/traceability/type/Tool';

type ReferenceSummaryProps = {
  tools: Tool[];
  selectedReference: string;
  isLoading: boolean;
};

export function ReferenceSummary({ tools, selectedReference, isLoading }: ReferenceSummaryProps) {
  const { t } = useTranslation();
  // tools contains traces
  const totalTraces = tools
    .filter((tool) => tool.reference === selectedReference)
    .reduce((sum, tool) => sum + tool.traces.length, 0);

  if (isLoading || !selectedReference) {
    return null;
  }

  // Calculate unique tools for the selected reference
  const uniqueTools = tools.filter((tool) => tool.reference === selectedReference).length;

  // Compute average traces per tool
  const avgTracesPerTool = uniqueTools > 0 ? (totalTraces / uniqueTools).toFixed(1) : '—';

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {/* Unique Tools */}
      <Card padding="none" className="flex items-center gap-3 p-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Package className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">
            {t('analytics.dashboard.summary.uniqueTools')}
          </p>
          <p className="text-lg font-semibold text-foreground">{uniqueTools}</p>
        </div>
      </Card>

      {/* Total Traces */}
      <Card padding="none" className="flex items-center gap-3 p-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-info/10 text-info">
          <FileText className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">
            {t('analytics.dashboard.summary.totalTraces')}
          </p>
          <p className="text-lg font-semibold text-foreground">{totalTraces.toLocaleString()}</p>
        </div>
      </Card>

      {/* Avg traces per tool */}
      <Card padding="none" className="flex items-center gap-3 p-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success/10 text-success">
          <Activity className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">
            {t('analytics.dashboard.summary.avgTracesPerTool')}
          </p>
          <p className="text-lg font-semibold text-foreground">{avgTracesPerTool}</p>
        </div>
      </Card>
    </div>
  );
}
